/**
 * FloorPlanOverlay - Displays an interactive floor plan with stage markers
 *
 * Features:
 * - Mini-map view (collapsed, always visible)
 * - Full-screen view (expanded on click)
 * - Click markers to navigate to stages
 * - Current stage highlighted with pulse animation
 * - Multi-floor support with floor switcher
 */

import type {
  NavigationConfig,
  NavigationMarker,
  FloorPlanConfig,
  ExperienceStage
} from '../types/config';

export interface FloorPlanOverlayEvents {
  onMarkerClick: (stageId: string) => void;
  onMarkerHover: (stageId: string | null) => void;
  onExpandToggle: (isExpanded: boolean) => void;
  onMinimapClick?: () => void;  // Click on minimap opens stage selector
}

export class FloorPlanOverlay {
  private container: HTMLElement;
  private config: NavigationConfig;
  private stages: ExperienceStage[];
  private events: FloorPlanOverlayEvents;

  private currentStageId: string | null = null;
  private currentFloorIndex: number = 0;
  private isExpanded: boolean = false;

  private overlayElement: HTMLElement | null = null;
  private minimapContainer: HTMLElement | null = null;
  private fullscreenContainer: HTMLElement | null = null;
  private tooltipElement: HTMLElement | null = null;

  constructor(
    container: HTMLElement,
    config: NavigationConfig,
    stages: ExperienceStage[],
    events: FloorPlanOverlayEvents
  ) {
    this.container = container;
    this.config = config;
    this.stages = stages;
    this.events = events;
  }

  /**
   * Render the floor plan overlay
   */
  public render(): void {
    // Determine if minimap should show - use new showMinimap or legacy displayMode
    const shouldShowMinimap = this.config.showMinimap !== undefined
      ? this.config.showMinimap
      : (this.config.displayMode !== 'fullscreen' && this.config.displayMode !== 'none');

    // If minimap is disabled, don't render anything (fullscreen is now in stage selector)
    if (!shouldShowMinimap) {
      return;
    }

    // Create main overlay container
    this.overlayElement = document.createElement('div');
    this.overlayElement.className = 'floor-plan-overlay';
    this.overlayElement.setAttribute('data-mode', 'minimap');

    // Create mini-map view (100x100px, bottom-left)
    this.createMinimapView();

    // Create tooltip element
    this.createTooltip();

    // Add to container
    this.container.appendChild(this.overlayElement);

    // Render markers
    this.renderMarkers();
  }

  /**
   * Create the mini-map view
   */
  private createMinimapView(): void {
    if (!this.overlayElement) return;

    // Minimap is now always 100x100px, bottom-left (hardcoded in CSS)
    this.minimapContainer = document.createElement('div');
    this.minimapContainer.className = 'floor-plan-minimap';

    const floorPlanUrl = this.getFloorPlanUrl();

    this.minimapContainer.innerHTML = `
      <div class="floor-plan-image-container">
        <img src="${floorPlanUrl}" alt="Floor Plan" class="floor-plan-image" />
      </div>
    `;

    // Click on minimap opens stage selector (will be handled by event)
    this.minimapContainer.addEventListener('click', () => {
      this.events.onMinimapClick?.();
    });

    this.overlayElement.appendChild(this.minimapContainer);
  }

  /**
   * Create tooltip element
   */
  private createTooltip(): void {
    if (!this.overlayElement) return;

    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'floor-plan-tooltip';
    this.tooltipElement.hidden = true;
    this.overlayElement.appendChild(this.tooltipElement);
  }

  /**
   * Render markers on minimap using HTML-positioned teardrop pins
   */
  private renderMarkers(): void {
    // Clear existing markers from minimap
    const imageContainer = this.minimapContainer?.querySelector('.floor-plan-image-container');
    if (!imageContainer) return;

    // Remove old pin elements
    imageContainer.querySelectorAll('.floor-plan-pin, .floor-plan-pin-label').forEach(el => el.remove());

    this.config.markers.forEach((marker) => {
      if (!marker.floorPlanPosition) return;

      const isCurrent = marker.stageId === this.currentStageId;
      const pinColor = marker.color || (isCurrent ? '#FF3B30' : '#34C759');

      // Create teardrop pin SVG (scaled down for minimap)
      const pinSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      pinSvg.setAttribute('class', `floor-plan-pin ${isCurrent ? 'current' : ''}`);
      pinSvg.setAttribute('data-stage-id', marker.stageId);
      pinSvg.setAttribute('viewBox', '0 0 24 32');
      pinSvg.style.cssText = `
        position: absolute;
        left: ${marker.floorPlanPosition.x * 100}%;
        top: ${marker.floorPlanPosition.y * 100}%;
        width: ${isCurrent ? '16px' : '12px'};
        height: ${isCurrent ? '20px' : '16px'};
        transform: translate(-50%, -100%);
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.5));
        cursor: pointer;
        transition: transform 0.15s ease, filter 0.15s ease;
        pointer-events: auto;
        z-index: ${isCurrent ? 2 : 1};
      `;

      // Teardrop path (same as editor)
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', pinColor);
      path.setAttribute('d', 'M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z');
      pinSvg.appendChild(path);

      // White center dot
      const centerDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      centerDot.setAttribute('fill', 'white');
      centerDot.setAttribute('cx', '12');
      centerDot.setAttribute('cy', '12');
      centerDot.setAttribute('r', isCurrent ? '3' : '2');
      pinSvg.appendChild(centerDot);

      // Hover effects
      pinSvg.addEventListener('mouseenter', (e) => {
        pinSvg.style.transform = 'translate(-50%, -100%) scale(1.2)';
        pinSvg.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))';
        this.events.onMarkerHover(marker.stageId);
        this.showTooltip(marker, e as MouseEvent);
      });
      pinSvg.addEventListener('mouseleave', () => {
        pinSvg.style.transform = 'translate(-50%, -100%)';
        pinSvg.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))';
        this.events.onMarkerHover(null);
        this.hideTooltip();
      });

      // Click handler
      pinSvg.addEventListener('click', (e) => {
        e.stopPropagation();
        this.events.onMarkerClick(marker.stageId);
        if (this.isExpanded) {
          this.setExpanded(false);
        }
      });

      imageContainer.appendChild(pinSvg);
    });
  }

  /**
   * Show tooltip near marker
   */
  private showTooltip(marker: NavigationMarker, event: MouseEvent): void {
    if (!this.tooltipElement) return;

    const stage = this.stages.find(s => s.id === marker.stageId);
    const label = marker.label || stage?.name || marker.stageId;

    this.tooltipElement.textContent = label;
    this.tooltipElement.hidden = false;

    // Position tooltip near cursor
    const rect = this.overlayElement?.getBoundingClientRect();
    if (rect) {
      const x = event.clientX - rect.left + 10;
      const y = event.clientY - rect.top - 30;
      this.tooltipElement.style.left = `${x}px`;
      this.tooltipElement.style.top = `${y}px`;
    }
  }

  /**
   * Hide tooltip
   */
  private hideTooltip(): void {
    if (this.tooltipElement) {
      this.tooltipElement.hidden = true;
    }
  }

  /**
   * Set expanded/collapsed state
   */
  public setExpanded(expanded: boolean): void {
    this.isExpanded = expanded;

    if (this.overlayElement) {
      this.overlayElement.setAttribute('data-mode', expanded ? 'fullscreen' : 'minimap');
    }

    if (this.minimapContainer) {
      this.minimapContainer.hidden = expanded && this.config.displayMode === 'fullscreen';
    }

    if (this.fullscreenContainer) {
      this.fullscreenContainer.hidden = !expanded;
    }

    this.events.onExpandToggle(expanded);
  }

  /**
   * Update the current stage indicator
   */
  public updateCurrentStage(stageId: string): void {
    this.currentStageId = stageId;
    this.renderMarkers();
  }

  /**
   * Set current floor (for multi-floor buildings)
   */
  public setCurrentFloor(floorIndex: number): void {
    this.currentFloorIndex = floorIndex;

    // Update floor selector buttons
    const floorBtns = this.fullscreenContainer?.querySelectorAll('.floor-btn');
    floorBtns?.forEach((btn, index) => {
      btn.classList.toggle('active', index === floorIndex);
    });

    // Update floor plan image
    const url = this.getFloorPlanUrl();
    const images = this.overlayElement?.querySelectorAll('.floor-plan-image');
    images?.forEach(img => {
      (img as HTMLImageElement).src = url;
    });

    // Re-render markers (they may be different per floor)
    this.renderMarkers();
  }

  /**
   * Get floor plans array
   */
  private getFloorPlans(): FloorPlanConfig[] {
    if (this.config.floorPlans && this.config.floorPlans.length > 0) {
      return this.config.floorPlans;
    }
    if (this.config.floorPlan) {
      return [this.config.floorPlan];
    }
    return [];
  }

  /**
   * Get current floor plan URL
   */
  private getFloorPlanUrl(): string {
    const floorPlans = this.getFloorPlans();
    if (floorPlans.length > 0 && this.currentFloorIndex < floorPlans.length) {
      return floorPlans[this.currentFloorIndex].imageUrl;
    }
    return '';
  }

  /**
   * Check if overlay is currently expanded
   */
  public isCurrentlyExpanded(): boolean {
    return this.isExpanded;
  }

  /**
   * Dispose and clean up
   */
  public dispose(): void {
    this.overlayElement?.remove();
    this.overlayElement = null;
    this.minimapContainer = null;
    this.fullscreenContainer = null;
    this.tooltipElement = null;
  }
}
